/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useState, useCallback, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Alert } from 'reactstrap';
import { getErrorMessage } from '@utils/error';
import Dashboard, { DashboardSection } from '@g17eco/molecules/dashboard';
import { Loader } from '@g17eco/atoms/loader';
import NotAuthorised from '@routes/not-authorised';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { RunHeader } from '@apps/company-tracker/components/intelligence/insights-synthesis/RunHeader';
import { InsightsTable } from '@apps/company-tracker/components/intelligence/insights-synthesis/InsightsTable';
import {
  useGetRunWithInsightsQuery,
  useUpdateInsightStatusMutation,
  useRetryGenerationRunMutation,
} from '@api/insights-synthesis';
import { GenerationRunStatus, type InsightSynthesis } from '@g17eco/types/insights-synthesis';

interface RouteParams {
  initiativeId: string;
  batchId: string;
}

const POLLING_INTERVAL = 5000;

export const InsightsSynthesisRunRoute = () => {
  const { initiativeId, batchId } = useParams<RouteParams>();
  const history = useHistory();
  const isUserStaff = useAppSelector(isStaff);

  const [pollingEnabled, setPollingEnabled] = useState(true);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const { data, isLoading, error } = useGetRunWithInsightsQuery(
    { initiativeId, batchId },
    {
      pollingInterval: pollingEnabled ? POLLING_INTERVAL : 0,
    }
  );

  const isProcessing = data?.status === GenerationRunStatus.Processing;

  // Stop polling when run is no longer processing
  useEffect(() => {
    if (data && !isProcessing) {
      setPollingEnabled(false);
    }
  }, [data, isProcessing]);

  const [updateInsightStatus] = useUpdateInsightStatusMutation();
  const [retryRun] = useRetryGenerationRunMutation();

  const handleBack = () => {
    history.push(generateUrl(ROUTES.INSIGHTS_SYNTHESIS_LIST, { initiativeId }));
  };

  const handleApprove = useCallback(
    async (insight: InsightSynthesis) => {
      setApprovingId(insight._id);
      setActionError(null);
      try {
        await updateInsightStatus({ initiativeId, insightId: insight._id, status: 'approved' }).unwrap();
      } catch (error) {
        setActionError(getErrorMessage(error));
      } finally {
        setApprovingId(null);
      }
    },
    [updateInsightStatus, initiativeId]
  );

  const handleReject = useCallback(
    async (insight: InsightSynthesis) => {
      setRejectingId(insight._id);
      setActionError(null);
      try {
        await updateInsightStatus({ initiativeId, insightId: insight._id, status: 'rejected' }).unwrap();
      } catch (error) {
        setActionError(getErrorMessage(error));
      } finally {
        setRejectingId(null);
      }
    },
    [updateInsightStatus, initiativeId]
  );

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    setActionError(null);
    try {
      await retryRun({ initiativeId, batchId }).unwrap();
      setPollingEnabled(true);
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setIsRetrying(false);
    }
  }, [retryRun, initiativeId, batchId]);

  if (!isUserStaff) {
    return <NotAuthorised />;
  }

  const renderContent = () => {
    if (error) {
      return <Alert color='danger'>Failed to load run details. Please try again.</Alert>;
    }

    if (isLoading) {
      return <Loader />;
    }

    if (!data) {
      return <Alert color='warning'>No data available for this run.</Alert>;
    }

    return (
      <>
        <div className='mb-3'>
          <Button color='link' className='p-0' onClick={handleBack}>
            <i className='fa fa-arrow-left me-1' /> Back to Runs
          </Button>
        </div>
        {actionError && (
          <Alert color='danger' toggle={() => setActionError(null)} className='mb-3'>
            {actionError}
          </Alert>
        )}
        <RunHeader
          run={data}
          isProcessing={isProcessing ?? false}
          onRetry={handleRetry}
          isRetrying={isRetrying}
        />
        <InsightsTable
          insights={data.insights}
          onApprove={handleApprove}
          onReject={handleReject}
          approvingId={approvingId}
          rejectingId={rejectingId}
        />
      </>
    );
  };

  return (
    <Dashboard>
      <DashboardSection>{renderContent()}</DashboardSection>
    </Dashboard>
  );
};
